import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiArrowRight, mdiPencil, mdiSourceMerge } from '@mdi/js'
import { Button } from '../ui/button'
import { SourceChip, StatusBadge } from './primitives'
import { CONF_THRESHOLD, type ReliabilityChange } from './useReliability'

type Props = {
  org: string
  change: ReliabilityChange
  disabled?: boolean
  onAccept: (candidateUid: string) => void
  onKeepCurrent: () => void
}

/**
 * One card renders all four reconciliation cases (spec §8.1): new value,
 * conflict, low-confidence, modification. Branch on `current` and
 * `candidates.length`, never on a kind enum.
 */
export function ChangeCard({ org, change, disabled, onAccept, onKeepCurrent }: Props) {
  const { t } = useTranslation('data-reliability')
  const { field, current, candidates } = change
  const multi = candidates.length > 1
  const lowConf = candidates.find((c) => c.confidence != null && c.confidence < CONF_THRESHOLD)
  const [pick, setPick] = useState<number | null>(multi ? null : 0)

  const head = current == null
    ? multi
      ? { tone: 'warning' as const, label: t('card.badge_conflict'), hint: t('card.hint_conflict') }
      : lowConf
        ? { tone: 'warning' as const, label: t('card.badge_to_fill'), hint: t('card.hint_low_confidence') }
        : { tone: 'primary' as const, label: t('card.badge_to_fill'), hint: t('card.hint_to_fill') }
    : { tone: 'primary' as const, label: t('card.badge_modification'), hint: t('card.hint_modification') }

  const borderTone = head.tone === 'warning' ? 'border-warning/40' : 'border-primary/40'
  const canResolve = !disabled && (!multi || pick !== null)

  const accept = () => {
    const idx = pick ?? 0
    onAccept(candidates[idx].uid)
  }

  return (
    <div className={`@container mb-3 rounded-xl border ${borderTone} bg-card p-4`} data-testid={`dr-change-${field}`}>
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <AppIcon path={mdiSourceMerge} size="sm" className={head.tone === 'warning' ? 'text-warning' : 'text-primary'} />
        <StatusBadge tone={head.tone}>{head.label}</StatusBadge>
        {lowConf && (
          <StatusBadge tone="warning">
            {t('card.badge_confidence', { pct: Math.round((lowConf.confidence ?? 0) * 100) })}
          </StatusBadge>
        )}
        <span className="text-sm font-semibold text-foreground">{field}</span>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">{head.hint}</p>

      <div className="mb-3 flex flex-col gap-3 @2xl:flex-row @2xl:items-stretch">
        {/* current / registry */}
        <div className={`min-w-0 flex-1 rounded-lg border border-border bg-muted/40 p-3 ${current ? 'opacity-90' : 'opacity-70'}`}>
          <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            {current ? t('card.current_registry') : t('card.registry_empty')}
          </div>
          {current ? (
            <>
              <div className="mb-1.5 text-base font-bold text-foreground">{current.value}</div>
              <SourceChip org={org} source={current.source} />
            </>
          ) : (
            <div className="text-sm font-medium text-muted-foreground">{t('card.not_filled')}</div>
          )}
        </div>

        <div className="grid place-items-center text-muted-foreground">
          <AppIcon path={mdiArrowRight} size="md" className="rotate-90 @2xl:rotate-0" />
        </div>

        {/* candidate(s) */}
        <div className={`flex min-w-0 flex-col gap-2 @md:flex-row @md:flex-wrap ${multi ? '@2xl:flex-[1.3]' : 'flex-1'}`}>
          {candidates.map((c, i) => {
            const sel = pick === i
            const active = sel || !multi
            return (
              <button
                key={c.uid}
                type="button"
                onClick={() => multi && setPick(i)}
                className={`flex-1 rounded-lg border p-3 text-left transition-colors ${
                  active ? 'border-primary bg-primary/10' : 'border-border bg-muted/40 hover:border-primary/50'
                } ${multi ? 'cursor-pointer' : 'cursor-default'}`}
                data-testid={`dr-candidate-${field}-${i}`}
              >
                <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                  {multi ? t('card.candidate', { n: i + 1 }) : t('card.proposed')}
                </div>
                <div className="mb-1.5 text-base font-bold text-foreground">{c.value}</div>
                <SourceChip org={org} source={c.source} />
              </button>
            )
          })}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          onClick={accept}
          disabled={!canResolve}
          data-testid={`dr-confirm-${field}`}
        >
          {current ? t('card.accept_candidate') : multi ? t('card.validate_selected') : t('card.confirm')}
        </Button>
        {current ? (
          <Button size="sm" variant="outline" onClick={onKeepCurrent} disabled={disabled} data-testid={`dr-keep-${field}`}>
            {t('card.keep_current')}
          </Button>
        ) : (
          <Button size="sm" variant="outline" onClick={onKeepCurrent} disabled={disabled} data-testid={`dr-reject-${field}`}>
            <AppIcon path={mdiPencil} size="sm" className="mr-1" />
            {t('card.correct')}
          </Button>
        )}
      </div>
    </div>
  )
}
